import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { BACKEND_DATE_FORMAT, HttpMethod } from './backend.constants';
import { HspBackendCall } from './wee-backend.call';
import { Alert, AlertService, AlertType } from '@michelin/theme';
import { environment } from 'src/environments/environment';

class ParamPair {
    key: string;
    value: boolean | number | string;
}

interface RequestOptions {
    headers: HttpHeaders;
    responseType: string;
    params?: HttpParams;
    body?: any;
    observe: string;
}

interface CoreConfiguration {
    apiBaseUrl: string;
}

@Injectable()
export class BackendService {
    private config : CoreConfiguration = {
        apiBaseUrl: environment.apiUrl
    }

    public constructor(
        private readonly dateAdapter: DateAdapter<Date>,
        private readonly httpClient: HttpClient,
        private readonly alertService: AlertService) {}

    /**
     * Executes the backend call passed by parameter, returning an observable that will return
     * the backend call response.
     *
     * @param call backend call to be executed.
     * @returns Observable of the response specified in the implementation of the call.
     */
    public executeCall<B extends HspBackendCall<I, O>, I, O>(call: B, apiBaseUrl?: string ): Observable<O> {

        return this.httpClient.request<O>(
            HttpMethod[call.method],
            this.buildApiUrl(call.endpoint, apiBaseUrl),
            this.buildRequestOptions(call),
        ).pipe(
            catchError((response: HttpErrorResponse) =>{
                if(response.status== HttpStatusCode.InternalServerError){
                    let error = response.error && response.error.errors ? response.error.errors[0] : null
                    if(error && error.message == 'Acceso denegado'){
                        this.alertService.showAlert({
                            type: AlertType.critical,
                            content: 'No dispone de los permisos para realizar esta operación.'
                        } as Alert);
                        return of({})
                    }
                }
                return throwError(() => response); 
            }),
            map((response: HttpResponse<O>) => response.body),
            tap((response: O) => call.output = response)
        );
    }

    private buildRequestOptions<B extends HspBackendCall<I, O>, I, O>(call: B): any {
        const headers = new HttpHeaders({
            'Content-Type': call.contentType,
            accept: '*/*',
        });
        let options: RequestOptions;

        switch(call.method) {
        case HttpMethod.POST:
        case HttpMethod.PUT:
            options = {headers, responseType: 'json', body: call.input, observe: 'response'};
            break;
        case HttpMethod.GET:
        case HttpMethod.DELETE:
            options = {headers, responseType: 'json', params: this.parseParams(call.input), observe: 'response'};
            break;
        default: throw new Error('HttpMethod not valid');
        }

        return options;
    }

    private parseParams<T>(input: T): HttpParams {
        let params = new HttpParams();
        if (input != null) {
            this.extractValidParams(input).forEach((param: ParamPair) =>
                params = params.set(param.key, param.value));
        }
        return params;
    }

    private extractValidParams<T>(input: T): ParamPair[] {
        return Object.keys(input)
            .filter((key: string) => this.isParameterValid(key, input))
            .map((key: string) => this.formatParameter(key, input));
    }

    private isParameterValid<T>(key: string, input: any): boolean {
        return input[key] !== null && input[key] !== '';
    }

    private formatParameter<T>(key: string, input: any): ParamPair {
        const param = new ParamPair();
        param.key = key;

        if (this.dateAdapter.isDateInstance(input[key])) {
            param.value = this.dateAdapter.format(input[key] as Date, BACKEND_DATE_FORMAT);
        } else if (typeof input[key] === 'number') {
            param.value = input[key] as number;
        } else if (typeof input[key] === 'boolean') {
            param.value = input[key] as boolean;
        } else if (typeof input[key] === 'string') {
            param.value = input[key] as string;
        } else if (Array.isArray(input[key]) && input[key].every((item: any) => typeof item === "string")){
            param.value = input[key].join(', ');
        }

        if (param.value !== null) {
            return param;
        } else {
            throw new Error('Trying to send an invalid type parameter to backend. Only accepted: number | string | boolean | Date | string[]');
        }
    }

    private buildApiUrl(endpoint: string, apiBaseUrl = this.config.apiBaseUrl): string {
        return apiBaseUrl + '/' + endpoint;
    }
}
